import { type InvestmentAccountAttributes } from '@egw/api'
import { ModalDrawer } from '@egw/components'
import { type FeatureFlags } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { useCallback, useMemo, type FC } from 'react'
import { nullTransferFieldOptionv2 } from '../../constants'
import { useTransferContextProvider } from '../../context'
import { EModalType, TransferField, type TransferListOption } from '../../types'
import { checkIfAchAccount, checkIfInvestmentAccount } from '../../utils'
import { TransferOptions } from './transfer-options'

export const TransferOptionsDrawer: FC = () => {
  const { t } = useTranslation('features-transfer')
  const { flags } = useFlags<FeatureFlags>()
  const {
    toFieldOption,
    fromFieldOption,
    allFieldOptions,
    selectOption,
    resetFieldOption,
    transferOptionsDrawerOpen,
    closeTransferOptionsDrawer,
    formMethods,
  } = useTransferContextProvider()

  // TO BE FETCHED AND FILTERED
  const toFieldOptions = useMemo(
    () =>
      fromFieldOption.value !== nullTransferFieldOptionv2.value
        ? allFieldOptions.map((transferableOption) => {
            const isInvestmentAccount = checkIfInvestmentAccount(transferableOption.accountType)
            return {
              ...transferableOption,
              isDisabled:
                // Disabled field options are those that From Field Option cannot transfer to
                // or the selected fromFieldOption itself
                !fromFieldOption.transferableToAccounts.includes(transferableOption.accountType) ||
                transferableOption.value === fromFieldOption.value ||
                transferableOption.isDisabled ||
                // Disable accounts that are not allowed to be deposited into (Apex flow)
                (fromFieldOption.transferableToAccounts.includes(transferableOption.accountType) &&
                  isInvestmentAccount &&
                  !(transferableOption.attributes as InvestmentAccountAttributes).clientDepositAllowed),
            }
          })
        : [],
    [allFieldOptions, fromFieldOption],
  )

  const options = useMemo(() => {
    if (transferOptionsDrawerOpen === EModalType.To) {
      // TO OPTIONS (DEPOSIT)
      return toFieldOptions
    }

    // FROM OPTIONS (WITHDRAWAL) - Apex flow
    const externalAccounts = allFieldOptions.filter((account) => checkIfAchAccount(account.accountType))

    // Filter All Internal Accounts for Investment Accounts that are allowed to be withdrawn
    const apexWithdrawableAccounts = allFieldOptions
      .filter((account) => checkIfInvestmentAccount(account.accountType))
      .filter((account) => (account.attributes as InvestmentAccountAttributes).clientWithdrawalAllowed)

    return [...externalAccounts, ...apexWithdrawableAccounts]
  }, [transferOptionsDrawerOpen, toFieldOptions, allFieldOptions])

  const checkFormCompletion = useCallback(() => {
    const fromValue = formMethods.getValues(TransferField.From)
    const toValue = formMethods.getValues(TransferField.To)
    const amountValue = formMethods.getValues(TransferField.Amount)

    return Boolean(fromValue.value && toValue.value && amountValue)
  }, [formMethods])

  const selectFromOption = useCallback(
    (option: TransferListOption): void => {
      selectOption({ option, key: TransferField.From })
      if (
        // To Field Option is the same as the newly selected From Field Option
        // OR the newly selected From Field Option cannot transfer to the To Field Option Selected then RESET Fields
        toFieldOption.value === option.value ||
        !option.transferableToAccounts.includes(toFieldOption.accountType)
      ) {
        resetFieldOption({ key: TransferField.To })
        resetFieldOption({ key: TransferField.Amount })
      }
    },
    [resetFieldOption, selectOption, toFieldOption],
  )

  const selectToOption = useCallback(
    (option: TransferListOption): void => {
      selectOption({ option, key: TransferField.To })
    },
    [selectOption],
  )

  const onSelect = useCallback(
    (option: TransferListOption) => {
      if (transferOptionsDrawerOpen === EModalType.To) {
        selectToOption(option)
      } else {
        selectFromOption(option)
      }
      closeTransferOptionsDrawer()

      // Upon selecting a new option, clear errors on the amount field
      formMethods.clearErrors(TransferField.Amount)

      // If all fields are filled, revalidate the form to check for errors and update the submit button
      if (checkFormCompletion()) {
        void formMethods.trigger()
      }
    },
    [
      selectFromOption,
      selectToOption,
      transferOptionsDrawerOpen,
      closeTransferOptionsDrawer,
      formMethods,
      checkFormCompletion,
    ],
  )

  return (
    <ModalDrawer
      isOpen={Boolean(transferOptionsDrawerOpen)}
      title={t(transferOptionsDrawerOpen === EModalType.To ? 'transfer.to' : 'transfer.from')}
      onClose={() => {
        closeTransferOptionsDrawer()
      }}
    >
      <TransferOptions options={options} onSelect={onSelect} />
    </ModalDrawer>
  )
}
